import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { StoreContext } from '../../context/StoreContext.js';
import "./StoreProduct.css";
import { ProductContext } from '../../context/ProductContext.js';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as SolidHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as RegularHeart } from '@fortawesome/free-regular-svg-icons';
import { FavoriteContext } from '../../context/FavoriteContext.js';
import { useImage } from "../../context/ImageContext.js";
import { Helmet } from "react-helmet-async";
import Loader from '../../components/Loader.js';
import Sidebar from '../../components/Sidebar.js';

const StoreProduct = () => {
  const { getImageUrl } = useImage();
  const { kullanici_id, kategori } = useParams();
  const navigate = useNavigate();
  const { state, fetchStoreById, fetchStoreImage } = useContext(StoreContext);
  const { stores, loading, error, storee } = state;
  const { handleMaxPriceChange, handleMinPriceChange, filterByPrice, state: product } = useContext(ProductContext);
  const [hasMore, setHasMore] = useState(true);
  const [isValidStore, setIsValidStore] = useState(true);
  const { maxPrice, minPrice } = product;
  const { favorites, addToFavorite, removeFavorite } = useContext(FavoriteContext);

  // URL validasyonu fonksiyonu
  const validateStoreUrl = (kullanici_id) => {
    // Eğer kullanici_id "kategori:" ile başlıyorsa, bu bir kategori URL'idir
    if (kullanici_id?.startsWith('kategori:')) {
      const kategoriId = kullanici_id.split(':')[1];
      // Kategori sayfasına yönlendir
      navigate(`/kategori/${kategoriId}`, { replace: true });
      return false;
    }

    // Kullanici_id sayısal olmalı veya geçerli bir mağaza SEF'i olmalı
    if (!kullanici_id || (isNaN(kullanici_id) && kullanici_id.length < 3)) {
      setIsValidStore(false);
      return false;
    }

    return true;
  };

  const isFavorite = (product) => {
    return favorites.some(favProduct =>
      String(favProduct.urun_id) === String(product.id)
    );
  };

  const handleFavoriteToggle = async (event, product) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      if (isFavorite(product)) {
        const favoriteProduct = favorites.find(fav =>
          String(fav.urun_id) === String(product.id)
        );
        if (favoriteProduct) {
          await removeFavorite(favoriteProduct);
        }
      } else {
        await addToFavorite(product);
      }
    } catch (error) {
      console.error('Favori işlemi sırasında hata:', error);
    }
  };

  useEffect(() => {
    // URL validasyonunu ilk çalıştır
    if (!validateStoreUrl(kullanici_id)) {
      return;
    }

    if (kullanici_id && isValidStore) {
      fetchStoreById(kullanici_id, state.currentPage, kategori);
      fetchStoreImage(kullanici_id);
    }
  }, [kullanici_id, kategori, isValidStore]);

  const handleScroll = useCallback(_.throttle(() => {
    const scrollThreshold = window.innerWidth < 768 ? 1400 : 700;
    if (loading || !hasMore || !isValidStore) return;

    if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - scrollThreshold) {
      fetchStoreById(kullanici_id, state.currentPage, kategori);
    }
  }, 500), [loading, hasMore, kullanici_id, fetchStoreById, state.currentPage, isValidStore]);

  useEffect(() => {
    if (isValidStore) {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, isValidStore]);

  // Geçersiz mağaza URL'i için hata sayfası
  if (!isValidStore) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning text-center">
          <h2>Geçersiz Mağaza URL'i</h2>
          <p>Aradığınız mağaza bulunamadı. Lütfen doğru URL'yi kontrol edin.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <Loader />;

  // API'den gelen hata kontrolü
  if (error) {
    navigate("/404");
  };

  // Stores array kontrolü
  const storeProducts = Array.isArray(stores) ? stores : [];

  return (
    <div className="store-page">
      <Helmet>
        <title>{storee ? `${storee.magaza_adi} Mağazası | BiKuyum` : 'Mağaza | BiKuyum'}</title>

        {/* Meta Description */}
        <meta
          name="description"
          content={storee ? `${storee.magaza_adi} mağazasının altın takı ve mücevher ürünleri. Güvenilir satıcı, kaliteli ürünler, uygun fiyatlar. BiKuyum'da güvenle alışveriş yapın.` : 'BiKuyum mağaza sayfası - güvenilir satıcılar, kaliteli ürünler.'}
        />

        {/* Meta Keywords */}
        <meta
          name="keywords"
          content={storee ? `${storee.magaza_adi}, altın mağaza, güvenilir satıcı, altın takı, mücevher, BiKuyum, online alışveriş` : 'altın mağaza, güvenilir satıcı, BiKuyum'}
        />

        {/* Robots Meta */}
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* Open Graph Meta Etiketleri */}
        <meta property="og:title" content={storee ? `${storee.magaza_adi} Mağazası | BiKuyum` : 'Mağaza | BiKuyum'} />
        <meta
          property="og:description"
          content={storee ? `${storee.magaza_adi} mağazasının altın takı ve mücevher ürünleri. Güvenilir satıcı, kaliteli ürünler, uygun fiyatlar.` : 'BiKuyum mağaza sayfası - güvenilir satıcılar, kaliteli ürünler.'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {storee && storee.kapak_fotografi && (
          <meta property="og:image" content={getImageUrl(storee.kapak_fotografi + "?size=full")} />
        )}
        <meta property="og:site_name" content="BiKuyum" />
        <meta property="og:locale" content="tr_TR" />

        {/* Twitter Card Meta Etiketleri */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={storee ? `${storee.magaza_adi} Mağazası | BiKuyum` : 'Mağaza | BiKuyum'} />
        <meta
          name="twitter:description"
          content={storee ? `${storee.magaza_adi} mağazasının altın takı ve mücevher ürünleri. Güvenilir satıcı, kaliteli ürünler, uygun fiyatlar.` : 'BiKuyum mağaza sayfası - güvenilir satıcılar, kaliteli ürünler.'}
        />
        {storee && storee.kapak_fotografi && (
          <meta name="twitter:image" content={getImageUrl(storee.kapak_fotografi + "?size=full")} />
        )}

        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />

        {/* JSON-LD Yapılandırılmış Veri */}
        {storee && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Store",
              "name": storee.magaza_adi,
              "description": `${storee.magaza_adi} mağazasının altın takı ve mücevher ürünleri.`,
              "url": window.location.href,
              "image": storee.kapak_fotografi ? getImageUrl(storee.kapak_fotografi + "?size=full") : null,
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "TR"
              },
              "makesOffer": storeProducts.slice(0, 10).map((product, index) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": product.baslik,
                  "image": getImageUrl(product.image.i1),
                  "url": `${window.location.origin}/urun/${product.sef}`
                },
                "price": product.fiyat,
                "priceCurrency": "TRY",
                "availability": product.stok > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
              })),
              "parentOrganization": {
                "@type": "Organization",
                "name": "BiKuyum"
              }
            })}
          </script>
        )}
      </Helmet>

      <div className="container-fluid px-0">
        <div className="magaza-photo mb-4">
          <div className="magaza-photo-img position-relative">
            {storee && storee.kapak_fotografi ? (
              <img src={getImageUrl(storee.kapak_fotografi + "?size=full")} alt="magaza-kapak-fotografi" className="img-fluid" />
            ) : (
              <div className="d-flex justify-content-center align-items-center h-100 bg-light">
                <p className="text-muted">Mağaza kapak fotoğrafı bulunamadı.</p>
              </div>
            )}
            {storee && (
              <div className="store-title-overlay">
                <h1 className="store-title">{storee.magaza_adi}</h1>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="store-container">
          <div className="store-list">
            <Sidebar />
          </div>

          <div className="store-product mt-3">
            {storeProducts.length > 0 ? (
              storeProducts.map((prodct, indx) => (
                <a
                  key={`${prodct.id}-${indx}`}
                  href={`/urun/${prodct.sef}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-product-card shadow-sm"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="store-img-container">
                    <img
                      src={getImageUrl(prodct.image.i1)}
                      alt={prodct.baslik}
                      onError={(e) => {
                        e.target.onerror = null;
                      }}
                      className="img-fluid"
                    />
                    
                    <div
                      onClick={(e) => handleFavoriteToggle(e, prodct)}
                      className="favorite-iconn d-flex"
                      title={isFavorite(prodct) ? "Favorilerden Çıkar" : "Favorilere Ekle"}
                    >
                      <FontAwesomeIcon
                        icon={isFavorite(prodct) ? SolidHeart : RegularHeart}
                        style={{ color: isFavorite(prodct) ? 'red' : 'gray' }}
                      />
                    </div>
                    {prodct.eski_fiyat !== prodct.fiyat && (
                      <div className="discount-badge">
                        {/* {Math.floor(((prodct.eski_fiyat - prodct.fiyat) / prodct.eski_fiyat) * 100)}% */}
                        <div className='d-flex flex-column'>
                          {(((prodct.eski_fiyat - prodct.fiyat) / prodct.eski_fiyat) * 100).toFixed(1)}%
                          <span>İndirim</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="store-responsive-info px-2">
                    <h3 className="store-responsive-name fw-bold">
                      {prodct.baslik}
                    </h3>
                    {/* Görüntülenme Metni */}
                    <div className='d-flex align-items-center gap-2 wiev-count' style={{ marginTop: '4px' }}>
                      <i className="fas fa-eye"></i>
                      <span>
                        {(prodct.view_count ?? 0).toLocaleString('tr-TR')} incelenme
                      </span>
                    </div>
                    <div className="ratingg">
                      {'★'.repeat(5)}
                    </div>
                    <div className="store-pricee">
                      {prodct.fiyat && prodct.eski_fiyat !== prodct.fiyat ? (
                        <div className='d-flex align-items-center gap-1'>
                          <p className="store-originall-price line-through m-0">
                            {prodct.eski_fiyat.toLocaleString('tr-TR')} TL
                          </p>
                          <p className="store-discountedd-price m-0">
                            {prodct.fiyat.toLocaleString('tr-TR')} TL
                          </p>
                        </div>
                      ) : (
                        <p className="store-originall-price">
                          {prodct.fiyat.toLocaleString('tr-TR')} TL
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              ))
            ) : (
              <div className="no-products p-4 bg-light rounded shadow-sm text-center w-100">
                <div className="text-muted">
                  <i className="bi bi-emoji-frown fs-1 d-block mb-3"></i>
                  <p className="mb-0">Bu kategoride ürün bulunamadı.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreProduct;